import { FC, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Breadcrumbs, SortingState } from '@dbt-labs/sourdough';

import {
  ProjectRecommendationSortField,
  RuleCategoryEnum,
  RuleNameEnum,
  RuleSeverityEnum,
  SortDirection,
} from '@/__generated__/graphql';
import { Page } from '@/components/Page';
import { PageHeading } from '@/components/PageHeading';
import { MetricSection } from '@/components/Recommendations/MetricSection';
import { RecommendationsTable } from '@/components/Recommendations/RecommendationTable';
import {
  RecommendationTableFilter,
  SelectedFilters,
} from '@/components/Recommendations/RecommendationTableFilter';
import { DetailsSection } from '@/components/SectionWithCard';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useRecommendations } from '@/hooks/discovery/recommendations';
import { useProjectBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { truthy } from '@/util/array';

const INITIAL_PAGE_SIZE = 10;

export interface RecommendationsPageProps {}

const SORTABLE_COLS = {
  severity: ProjectRecommendationSortField.Severity,
} satisfies Record<string, ProjectRecommendationSortField>;

type SortableColumn = keyof typeof SORTABLE_COLS;

const isSortableColumn = (input: string): input is SortableColumn => {
  return Object.keys(SORTABLE_COLS).includes(input);
};

export const RecommendationsPage: FC<RecommendationsPageProps> = () => {
  const { accountId, projectName } = useDbtCloudProject();

  const breadcrumbs = useProjectBreadcrumbs({
    additionalItems: [
      {
        text: 'Recommendations',
      },
    ],
  });

  if (!accountId) {
    return null;
  }

  return (
    <Page pageType="recommendations" title={`${projectName} - Recommendations`}>
      <Breadcrumbs className="mb-3" breadcrumbs={breadcrumbs} />
      <RecommendationsPageContent />
    </Page>
  );
};

type RecommendationsPageContentProps = {
  showHeader?: boolean;
};

export const RecommendationsPageContent: FC<RecommendationsPageContentProps> = ({
  showHeader = true,
}) => {
  const { environmentId, accountId } = useDbtCloudProject();

  const pageSize = INITIAL_PAGE_SIZE;
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>();

  useEffect(() => {
    setSelectedFilters({
      category: [searchParams.get('category')].filter(truthy) as RuleCategoryEnum[],
      ruleName: [searchParams.get('ruleName')].filter(truthy) as RuleNameEnum[],
      severity: [searchParams.get('severity')].filter(truthy) as RuleSeverityEnum[],
    });
  }, [searchParams]);

  const [sort, setSort] = useState({
    field: ProjectRecommendationSortField.Severity,
    direction: SortDirection.Desc,
  });

  const violations = useRecommendations({
    environmentId: environmentId || 0,
    first: pageSize,
    filter: {
      ruleNames: selectedFilters?.ruleName.length ? selectedFilters.ruleName : null,
      severity: selectedFilters?.severity.length ? selectedFilters.severity : null,
      categories: selectedFilters?.category.length ? selectedFilters.category : null,
    },
    sort,
  });

  const onChangeSort = useCallback(
    (sortRule: SortingState) => {
      if (!sortRule?.length) {
        return;
      }
      const sortCol: string = sortRule[0].id;
      const sortDir = sortRule[0].desc ? SortDirection.Desc : SortDirection.Asc;
      if (
        isSortableColumn(sortCol) &&
        (sort.field !== sortCol || sort.direction !== sortDir)
      ) {
        setSort({
          field: SORTABLE_COLS[sortCol],
          direction: sortDir,
        });
      }
    },
    [sort.direction, sort.field],
  );

  if (!accountId) {
    return null;
  }

  const getRecommendationsTitle = (count: number | undefined) => {
    if (count === undefined) return '';
    if (count === 1) return '1 Recommendation';
    return `${count} Recommendations`;
  };

  const header = getRecommendationsTitle(violations.pageInfo?.totalCount ?? undefined);

  return (
    <>
      {showHeader && <PageHeading className="mb-2">Recommendations</PageHeading>}
      <DetailsSection heading="Project summary" withCard={false}>
        <MetricSection environmentId={environmentId || 0} />
      </DetailsSection>
      <div className="mt-4">
        <RecommendationTableFilter
          className="mb-4"
          header={header}
          selectedFilters={selectedFilters}
          onChange={(filters) => {
            setSearchParams((searchParams) => {
              if (filters.category.length === 0) {
                searchParams.delete('category');
              } else {
                searchParams.set('category', filters.category.join(','));
              }
              if (filters.severity.length === 0) {
                searchParams.delete('severity');
              } else {
                searchParams.set('severity', filters.severity.join(','));
              }
              if (filters.ruleName.length === 0) {
                searchParams.delete('ruleName');
              } else {
                searchParams.set('ruleName', filters.ruleName.join(','));
              }
              return searchParams;
            });
          }}
        />
        <RecommendationsTable
          violationsQuery={violations}
          pageSize={pageSize}
          onChangeSort={onChangeSort}
        />
      </div>
    </>
  );
};
